import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import { CreatorOptions, CreatorState, CreatorStep } from '@/types/creator';
import { ProductType } from '@/api/ProductTypeApi';
import { UUID } from '@/types/types';
import { AttributeValue } from '@/api/AttributeValueApi';
import {
	setAttributeValuesAvailability,
	setMathinchVariants,
	unselectNextAttributes,
} from '@/utils/creatorUtilities';

const initialState: CreatorState = {
	selectedProductType: null,
	selectedProductTypeVariants: [],
	availableProductTypeVariants: [],
	creatorAttributes: [],
	selectedAttributeValues: [{}],
	optionalProductComponents: {},
	quantity: 1,
	price: 0,
	options: {
		currentStep: CreatorStep.PRODUCT_TYPES,
		isLoading: false,
		infoModalOpened: false,
		infoModalData: {
			title: '',
			description: '',
			imagePath: undefined,
		},
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		selectProductType: (state, { payload }: PayloadAction<ProductType>) => {
			state.selectedProductType = payload;
		},
		openInfoModal: (
			state,
			{
				payload,
			}: PayloadAction<{
				title: string;
				description: string;
				imagePath?: string;
			}>
		) => {
			state.options.infoModalData = payload;
			state.options.infoModalOpened = true;
		},
		selectAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				attributeId: UUID;
				value: AttributeValue;
				circulationIndex: number | null;
			}>
		) => {
			if (payload.circulationIndex === null) {
				for (const selectedValues of state.selectedAttributeValues) {
					selectedValues[payload.attributeId] = payload.value;
				}
			} else {
				state.selectedAttributeValues[payload.circulationIndex][
					payload.attributeId
				] = payload.value;
			}

			unselectNextAttributes(payload.attributeId, state);
			setAttributeValuesAvailability(state);
		},
		setProductComponentQuantity: (
			state,
			{ payload }: PayloadAction<{ componentId: UUID; quantity: number }>
		) => {
			state.optionalProductComponents[payload.componentId] = payload.quantity;
		},
		setCreatorStep: (state, { payload }: PayloadAction<'prev' | 'next'>) => {
			const { options, creatorAttributes } = state;

			if (payload === 'prev') {
				if (options.currentStep === CreatorStep.PRODUCT_TYPES) return;

				if (options.currentStep === CreatorStep.SUMMARY) {
					options.currentStep = creatorAttributes.at(-1)!.id;
					return;
				}

				const currentStepAttributeIndex = creatorAttributes.findIndex(
					(attribute) => attribute.id === options.currentStep
				);
				if (currentStepAttributeIndex === -1) return;

				const prevAttribute = creatorAttributes[currentStepAttributeIndex - 1];
				if (!prevAttribute) {
					options.currentStep = CreatorStep.PRODUCT_TYPES;
					return;
				}

				options.currentStep = prevAttribute.id;

				return;
			}

			if (payload === 'next') {
				if (options.currentStep === CreatorStep.SUMMARY) return;

				if (options.currentStep === CreatorStep.PRODUCT_TYPES) {
					options.currentStep = creatorAttributes[0]?.id;
					return;
				}

				const currentStepAttributeIndex = creatorAttributes.findIndex(
					(attribute) => attribute.id === options.currentStep
				);
				if (currentStepAttributeIndex === -1) return;

				const nextAttribute = creatorAttributes[currentStepAttributeIndex + 1];
				if (!nextAttribute) {
					setMathinchVariants(state);
					options.currentStep = CreatorStep.SUMMARY;
					return;
				}

				options.currentStep =
					creatorAttributes[currentStepAttributeIndex + 1].id;
			}
		},
		resetCreator: (state) => {
			state.selectedProductType = null;
			state.selectedAttributeValues = [{}];
			state.options.currentStep = CreatorStep.PRODUCT_TYPES;
			state.creatorAttributes = [];
			state.selectedProductTypeVariants = [];
			state.availableProductTypeVariants = [];
		},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
